/**
 * Groups admin page (F6a — split from UsersPage).
 *
 * Single-purpose page: list groups + create / edit (via GroupDialog)
 * + manage members (via GroupMembersDialog). Permissions UX lives
 * inside GroupDialog and gets the matrix + tag-scope rewrite in
 * F6b/F6c.
 *
 * The user-detail page (``/users/{id}``) still hosts the
 * "groups this user belongs to" tab — that stays because it's
 * scoped to a specific user. This page is the operator's view
 * across ALL groups.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Users, UsersRound } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { GroupDialog, type GroupDialogMode } from "@/components/users/GroupDialog";
import { GroupMembersDialog } from "@/components/users/GroupMembersDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { usersApi, type GroupSummary } from "@/lib/api-users";
import { usePaginationState } from "@/lib/pagination";

export function GroupsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [groupDialog, setGroupDialog] = React.useState<{ open: boolean; mode: GroupDialogMode }>({
    open: false,
    mode: { kind: "create" },
  });
  const [membersGroup, setMembersGroup] = React.useState<GroupSummary | null>(null);

  const groupsPage = usePaginationState();
  const groups = useQuery({
    queryKey: ["groups", groupsPage.page, groupsPage.pageSize],
    queryFn: () =>
      usersApi.listGroups({ page: groupsPage.page, page_size: groupsPage.pageSize }),
  });

  const groupItems = groups.data?.items ?? [];
  const groupTotal = groups.data?.total ?? 0;

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => usersApi.deleteGroup(groupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["groups"] }),
  });

  return (
    <PageContainer>
      <PageHeader
        title={t("groupsPage.title")}
        description={t("groupsPage.subtitle")}
        actions={
          <Button
            size="sm"
            onClick={() => setGroupDialog({ open: true, mode: { kind: "create" } })}
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("users.newGroup")}
          </Button>
        }
      />
      <PageContent>
        {groupItems.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Group</Th>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th className="w-24 text-right">Permissions</Th>
                  <Th className="w-44 text-right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groupItems.map((g) => (
                  <Tr key={g.group_id}>
                    <Td className="font-mono text-xs">
                      {g.group_id}
                      {g.group_id === "admin" && (
                        <Badge tone="warning" className="ml-2">
                          admin
                        </Badge>
                      )}
                    </Td>
                    <Td>{g.name}</Td>
                    <Td className="text-muted-foreground">{g.description ?? "—"}</Td>
                    <Td className="text-right text-muted-foreground tabular-nums">
                      {g.permissions.length}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setMembersGroup(g)}
                          title="Manage members"
                        >
                          <UsersRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setGroupDialog({
                              open: true,
                              mode: { kind: "edit", group: g },
                            })
                          }
                          title="Edit group"
                        >
                          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                        {g.group_id !== "admin" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={deleteGroupMutation.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete group "${g.name}"? Members must be removed first.`,
                                )
                              ) {
                                deleteGroupMutation.mutate(g.group_id);
                              }
                            }}
                            title="Delete group"
                          >
                            <Trash2
                              className="h-3.5 w-3.5 text-destructive"
                              strokeWidth={1.5}
                            />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={groupsPage.page}
              pageSize={groupsPage.pageSize}
              total={groupTotal}
              onPageChange={groupsPage.setPage}
              onPageSizeChange={groupsPage.setPageSize}
            />
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title={t("users.noGroups")}
            description="Create a group to assign users to."
          />
        )}
      </PageContent>

      <GroupDialog
        open={groupDialog.open}
        mode={groupDialog.mode}
        onOpenChange={(open) => setGroupDialog((d) => ({ ...d, open }))}
      />
      <GroupMembersDialog
        group={membersGroup}
        onOpenChange={(open) => !open && setMembersGroup(null)}
      />
    </PageContainer>
  );
}
